/**
 * A client engagement's project progress, for the Client record page. Moved
 * out of ClientDetail.tsx, where it was inline, so it is tested.
 *
 * Milestones carry no phase id, so they are spread evenly across the phases in
 * sort order: phase i holds milestones [i*per, (i+1)*per), per = ceil(n / phases).
 * A phase's progress is the mean of its milestones' progress (0 with none).
 * The current phase is the first under 100; when all are done it is the last.
 *
 * Due days. project_milestones.due_date is a timestamp the API sends as the
 * driver's ISO string. Its date part is the intended day (the app writes noon
 * Central, older rows midnight UTC; server/morningDigest.ts reads it the same
 * way), so it is never passed to new Date(), which would show a midnight-UTC
 * row a day early in Central.
 */
import { addDaysKey } from "./weeks";

export interface PhaseLike { id: string; name: string }
export interface MilestoneLike { id: string; name: string; progress: number | null; dueDate: string | null }

export type PhaseWithProgress<P, M> = P & { progress: number; milestones: M[] };

export interface ProjectProgress<P, M> {
  phases: PhaseWithProgress<P, M>[];
  overall: number; // 0-100, the mean of every milestone's progress
  currentIndex: number; // the first phase under 100, or -1 when every phase is done
  current: PhaseWithProgress<P, M> | null; // that phase, else the last; null without phases
}

export function projectProgress<P extends PhaseLike, M extends MilestoneLike>(phases: P[], milestones: M[]): ProjectProgress<P, M> {
  const total = milestones.length;
  const per = Math.ceil(total / (phases.length || 1));
  const withProgress = phases.map((phase, idx) => {
    const start = idx * per;
    const own = milestones.slice(start, Math.min(start + per, total));
    const progress = own.length > 0 ? Math.round(own.reduce((s, m) => s + (m.progress || 0), 0) / own.length) : 0;
    return { ...phase, progress, milestones: own };
  });
  const overall = total > 0 ? Math.round(milestones.reduce((s, m) => s + (m.progress || 0), 0) / total) : 0;
  const currentIndex = withProgress.findIndex((p) => p.progress < 100);
  const current = currentIndex >= 0 ? withProgress[currentIndex] : withProgress[withProgress.length - 1] ?? null;
  return { phases: withProgress, overall, currentIndex, current };
}

export const isDone = (m: { progress: number | null }) => (m.progress ?? 0) >= 100;

/** The intended day of a stored due date: its date part, or null. */
export function dueDay(dueDate: string | null | undefined): string | null {
  return dueDate && /^\d{4}-\d{2}-\d{2}/.test(dueDate) ? dueDate.slice(0, 10) : null;
}

export type DueTone = "bad" | "warn";

/** Past due is bad; due today through seven days out is a warning. Days are YYYY-MM-DD, today in Central. */
export function dueTone(day: string | null, today: string): DueTone | null {
  if (!day) return null;
  if (day < today) return "bad";
  return day <= addDaysKey(today, 7) ? "warn" : null;
}

function byDueDay(a: { day: string | null; i: number }, b: { day: string | null; i: number }): number {
  if (a.day && b.day && a.day !== b.day) return a.day < b.day ? -1 : 1;
  if (a.day && !b.day) return -1;
  if (!a.day && b.day) return 1;
  return a.i - b.i;
}

/** Open milestones, soonest due first, undated last; sort order breaks a tie. */
export function openMilestones<M extends MilestoneLike>(milestones: M[], limit = 5): M[] {
  return milestones
    .map((m, i) => ({ m, i, day: dueDay(m.dueDate) }))
    .filter(({ m }) => !isDone(m))
    .sort(byDueDay)
    .slice(0, limit)
    .map(({ m }) => m);
}

/** The nearest open milestone that has a due day, overdue ones included. */
export function nextMilestone<M extends MilestoneLike>(milestones: M[]): M | null {
  return openMilestones(milestones, Infinity).find((m) => dueDay(m.dueDate)) ?? null;
}
